<template>
	<div class="integration-container height-100">
		<ActionsMenuForEditNodeDataProvider
			v-if="showActionsMenu"
			v-on="$listeners"
			class="pt-9 py-12"
			:title="actionsMenuTitle"
			:selectedServiceActionCollectionId="selectedServiceActionCollectionId"
			:noBackButton="editingTrigger"
			:isTrigger="editingTrigger"
		/>

		<template v-else-if="showParamsEditor || showProfileSelection">
			<StepDetailsHeader
				v-on="$listeners"
				:icon="stepHeaderIcon"
				:hideProfileSelectionOption="!isProfileSelectionNeeded"
				:isContextualNode="isContextualNode"
			/>
			<template v-if="showParamsEditor">
				<div v-if="actionExtraInfo" class="extra-info-container">
					<span v-html="actionExtraInfo"></span>
				</div>
				<BlockContextualEditorParams v-if="isContextualNode" :node="editNode" :placement="placement" :workflowId="editWorkflow.id" />
				<BlockEditorParams v-on="$listeners" :showAdvancedSettings="!isProfileSelectionNeeded" v-else />
			</template>
			<ProfileSelection
				v-else-if="showProfileSelection"
				:noBackButton="isContextualNode || editingTrigger"
				:isSelectionForContextualData="isContextualNode"
			/>
		</template>

		<BlockEditorAccordionMenu v-on="$listeners" />

		<div v-if="isStepMenuOpen" @click="onMenuClose" class="faded-background" />
		<AppLoader v-if="isLoading" class="height-100 flex-centerize" />
	</div>
</template>

<script>
import { sanitizeHtml } from '@/utils/helpers';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { getService, getServiceActionCollection } from '@/store/modules/apps/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import { element } from '@/store/modules/selectedWorkflow/getters';
import { getAction } from '@/store/modules/actions/getters';
import { setEditorAccordionMenuItems, setIsEditorAccordionMenuOpen } from '@/store/modules/blockEditor/actions';

import AppService from '@/utils/AppService';

import StepDetailsHeader from './StepDetailsHeader';
import ActionsMenuForEditNodeDataProvider from './IntegrationEditor/ActionsMenuForEditNodeDataProvider';
import ProfileSelection from './IntegrationEditor/ProfileSelection';
import BlockEditorParams from './BlockEditorParams/BlockEditorParams';
import BlockContextualEditorParams from './BlockEditorParams/BlockContextualEditorParams';
// import AutomationTriggerEditor from '@/components/BlockEditor/AutomationTriggerEditor/AutomationTriggerEditor.vue';
import BlockEditorAccordionMenu from './helpers/BlockEditorAccordionMenu';

import AppActionService from '@/utils/AppActionService';
import { getPlacement } from '@/store/modules/placements/getters';

export default {
	name: 'IntegrationBlockEditorContent',
	props: {
		isStepMenuOpen: { type: Boolean },
		showParamsEditor: { type: Boolean },
		isContextualNode: { type: Boolean },
		selectedServiceActionCollectionId: { type: Number },
	},
	components: {
		ActionsMenuForEditNodeDataProvider,
		ProfileSelection,
		StepDetailsHeader,
		BlockEditorParams,
		BlockContextualEditorParams,
		// AutomationTriggerEditor,
		BlockEditorAccordionMenu,
	},
	computed: {
		...mapGettersShorthand({ getAction, getService, editNode, getServiceActionCollection, editWorkflow, element, getPlacement }),
		isLoading() {
			return !this.showActionsMenu && !this.showParamsEditor && !this.showProfileSelection;
		},
		showActionsMenu() {
			return (!this.editNode.action && !!this.editNode.service && !this.isWidget) || !!this.selectedServiceActionCollectionId;
		},
		showProfileSelection() {
			return !!this.editNode.action && this.isProfileSelectionNeeded && !!this.originalActionApp;
		},
		originalActionApp() {
			return this.getService(this.editNode.service);
		},
		selectedApp() {
			if (this.selectedServiceActionCollectionId || AppActionService.doesActionHasServiceActionCollection(this.selectedAction)) {
				const serviceActionCollectionId =
					this.selectedServiceActionCollectionId || AppActionService.getActionServiceActionCollection(this.selectedAction);
				return this.getServiceActionCollection(serviceActionCollectionId);
			}
			return this.getService(this.editNode.service);
		},
		selectedAction() {
			return this.getAction(this.editNode.action);
		},
		skipAuthAction() {
			return this.selectedAction && this.selectedAction.skip_auth;
		},
		isProfileSelectionNeeded() {
			return AppService.doesRequiresAuth(this.originalActionApp) && !this.skipAuthAction;
		},
		actionExtraInfo() {
			return this.selectedAction?.extra_info && sanitizeHtml(this.selectedAction.extra_info);
		},
		stepHeaderIcon() {
			return AppActionService.getActionIcon({ action: this.selectedAction, app: this.selectedApp, originalApp: this.originalActionApp });
		},
		editingTrigger() {
			return this.editWorkflow?.trigger_config?.node === this.editNode.id;
		},
		isTriggerConfigured() {
			return this.editWorkflow?.trigger_config?.subscribed;
		},
		isWidget() {
			return this.originalActionApp?.service_slug === 'widgets';
		},
		actionsMenuTitle() {
			return this.editingTrigger ? 'Choose trigger' : 'Choose action';
		},
		placement() {
			return this.getPlacement(this.element.placement);
		},
	},
	methods: {
		...mapActionsShorthand({ setEditorAccordionMenuItems, setIsEditorAccordionMenuOpen }),
		onMenuClose() {
			this.setEditorAccordionMenuItems(null);
			this.setIsEditorAccordionMenuOpen(false);
		},
	},
};
</script>

<style scoped lang="scss">
.extra-info-container {
	// order-matters
	padding: 20px;
	padding-bottom: 0;
}

.integration-container {
	overflow: hidden;
}
.profile-and-asnyc-container {
	height: 50px;
	font-size: 12px;
	color: var(--primary-font-color);
	border-bottom: 1px solid var(--border-color);
}

.faded-background {
	width: var(--widget-selector-modal-width);
	right: 0;
	bottom: 0;
	top: 120px;
	z-index: 50;
	position: fixed;
	background-color: var(--faded-background);
}
</style>
